import React from 'react';
/* eslint-disable no-return-assign */
import _uniq from 'lodash-es/uniq';
import {
  CarnetElevesSort,
  Evaluation,
  Note,
} from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import { Eleve } from '@teetsh/app/src/types/eleves';
import ButtonRadio from '@teetsh/app/src/components/Form/ButtonsRadio';
import Card from '@teetsh/app/src/components/Card';
import { useCDNContext } from '@teetsh/app/src/containers/CarnetsDeNotes/CarnetDeNotesContext';
import EleveNotes from '../../components/Notes/EleveNotes';
import tw from 'twin.macro';
import { useNoteRefs } from '../../components/Notes/useNoteRefs';
import {
  ensureNotesForStudent,
  ensureNotesForAllStudents,
} from '../../utils/ensureNotesForStudent';
import { SortButton } from 'components/SortElevesButton';
import { sortEleves } from 'containers/CarnetsDeNotes/CarnetDetails/sortEleves';
import { getSortFieldsOrder } from '@teetsh/app/src/containers/CarnetsDeNotes/CarnetDetails/Body/getSortFieldsOrder';
import { useTranslation } from 'react-i18next';

interface Props {
  eleves: Eleve[];
  onReplaceNotes: (notes: Note[]) => void;
  evaluation: Evaluation;
  elevesSort: CarnetElevesSort;
  onUpdateSort: (sort: CarnetElevesSort) => void;
}

export function Notes(props: Props) {
  const {
    eleves,
    evaluation,
    onReplaceNotes,
    elevesSort: sort,
    onUpdateSort,
  } = props;
  const { notationSystem } = useCDNContext();

  const { getInputRef, getNextInputRef } = useNoteRefs(evaluation.notes);
  const { t } = useTranslation(['carnet', 'eleve']);

  const allNotesUniq = _uniq(evaluation.notes.map(note => note.note));
  const allTheSameNote = allNotesUniq.length > 1 ? undefined : allNotesUniq[0];
  const referentialIds = _uniq(
    evaluation.notes
      .filter(n => n.referentialItemId != null)
      .map(n => n.referentialItemId),
  );
  const hasSpecificNotes = referentialIds.length > 0;
  return (
    <div>
      <div tw="flex justify-between pl-4 pr-2 mb-2 items-center">
        <div tw="w-full hidden md:block">
          {notationSystem.type === 'competency' && (
            <div
              data-testid="all-notes"
              tw="flex justify-between flex-col md:flex-row md:items-center flex-wrap"
            >
              <div tw="mr-5 truncate flex-1">
                <SortButton
                  fieldName={sort.fieldName}
                  onChange={({ fieldName, direction }) =>
                    onUpdateSort({ fieldName, direction })
                  }
                  fieldLabel={t(`eleve:fields.${sort.fieldName}`, {
                    defaultValue: '',
                  })}
                  direction={sort.direction}
                />
              </div>
              <div>
                <ButtonRadio
                  withoutMargin
                  small
                  value={allTheSameNote}
                  canBeEmpty
                  onChange={v => {
                    const eleveIds = eleves.map(e => e.id);
                    const allNotes = ensureNotesForAllStudents(
                      eleveIds,
                      evaluation.id,
                      evaluation.notes,
                      { notationSystem },
                    );
                    onReplaceNotes(
                      allNotes.map(n => ({
                        ...n,
                        note: v as number,
                      })),
                    );
                  }}
                  options={notationSystem.values.map(v => ({
                    label: v.label,
                    value: v.value,
                    tooltip: v.tooltip,
                    color: notationSystem.getNoteColorContrast(v.value),
                  }))}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {sortEleves(
        eleves,
        getSortFieldsOrder(sort?.fieldName),
        sort?.direction,
      ).map(eleve => {
        const eleveNotes = ensureNotesForStudent(
          eleve.id,
          evaluation.id,
          evaluation.notes,
          { evaluation, includeEnrichment: true, notationSystem },
        );
        return (
          <Card
            css={[
              hasSpecificNotes && tw`py-4!`,
              !hasSpecificNotes && tw`py-2!`,
              !eleve.isEnrolledAt?.(evaluation.date) &&
                tw`bg-neutral-supershy opacity-50`,
            ]}
            tw="pl-4! pr-2! mb-2"
            key={eleve.id}
          >
            <EleveNotes
              evaluationId={evaluation.id}
              evaluationDate={evaluation.date}
              eleve={eleve}
              notes={eleveNotes}
              getInputRef={getInputRef}
              getNextInputRef={getNextInputRef}
            />
          </Card>
        );
      })}
    </div>
  );
}
